<script setup lang="ts">
import { computed } from 'vue';

/** User display for avatar (subset of API UserInDb) */
export interface UserDisplay {
  full_name: string;
  email: string;
  uid?: string;
}

const props = withDefaults(
  defineProps<{
    /** User to show (full_name + email). If null, shows placeholder. */
    user: UserDisplay | null | undefined;
    /** Avatar size */
    size?: 'xs' | 'sm' | 'md' | 'lg';
    /** Show tooltip with full name and email on hover */
    showTooltip?: boolean;
    /** Optional: show display name next to avatar */
    showName?: boolean;
  }>(),
  { size: 'sm', showTooltip: true, showName: false }
);

/** Palette of soft, accessible background colors (same user = same color via hash) */
const AVATAR_COLORS = [
  '#e0f2fe',
  '#dbeafe',
  '#e0e7ff',
  '#ede9fe',
  '#fce7f3',
  '#fef3c7',
  '#d1fae5',
  '#ccfbf1',
  '#e2e8f0',
];

function getInitials(user: UserDisplay): string {
  const name = (user.full_name || '').trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      const first = parts[0].charAt(0);
      const second = parts[1].charAt(0);
      return (first + second).toUpperCase();
    }
    if (parts[0].length >= 2) {
      return parts[0].slice(0, 2).toUpperCase();
    }
    return parts[0].charAt(0).toUpperCase();
  }
  const email = (user.email || '').trim();
  if (email) {
    const local = email.split('@')[0] || '';
    return local.slice(0, 2).toUpperCase() || '?';
  }
  return '?';
}

function hashForColor(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + (str.codePointAt(i) ?? 0);
    h |= 0;
  }
  return Math.abs(h);
}

const initials = computed(() => (props.user ? getInitials(props.user) : '—'));

const backgroundColor = computed(() => {
  if (!props.user) return 'var(--n-color-embedded);';
  const seed = (props.user.full_name || props.user.email || props.user.uid) ?? '';
  const index = hashForColor(seed) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
});

const textColor = computed(() => {
  if (!props.user) return 'var(--n-text-color-3);';
  const seed = props.user.full_name || props.user.email || '';
  const index = hashForColor(seed) % AVATAR_COLORS.length;
  // Darker text for light backgrounds
  const darkShades = [
    '#0369a1',
    '#1d4ed8',
    '#3730a3',
    '#6b21a8',
    '#9d174d',
    '#b45309',
    '#047857',
    '#0f766e',
    '#475569',
  ];
  return darkShades[index];
});

const sizePx = computed(() => {
  switch (props.size) {
    case 'xs':
      return 20;
    case 'sm':
      return 28;
    case 'md':
      return 36;
    case 'lg':
      return 44;
    default:
      return 28;
  }
});

const displayName = computed(() =>
  props.user ? props.user.full_name || props.user.email || '' : ''
);

const tooltipText = computed(() => {
  if (!props.user) return '';
  const parts = [props.user.full_name, props.user.email].filter(Boolean);
  return parts.join(' · ') || '';
});
</script>

<template>
  <NTooltip v-if="showTooltip && user && tooltipText" trigger="hover" placement="top">
    <template #trigger>
      <div class="user-initials-avatar-wrapper">
        <span
          class="user-initials-avatar"
          :class="{ 'avatar-placeholder': !user }"
          :style="{
            width: sizePx + 'px',
            height: sizePx + 'px',
            minWidth: sizePx + 'px',
            fontSize: sizePx * 0.4 + 'px',
            backgroundColor: user ? backgroundColor : undefined,
            color: textColor,
          }"
        >
          {{ initials }}
        </span>
        <span v-if="showName && displayName" class="user-initials-name">{{ displayName }}</span>
      </div>
    </template>
    {{ tooltipText }}
  </NTooltip>
  <div v-else class="user-initials-avatar-wrapper">
    <span
      class="user-initials-avatar"
      :class="{ 'avatar-placeholder': !user }"
      :style="{
        width: sizePx + 'px',
        height: sizePx + 'px',
        minWidth: sizePx + 'px',
        fontSize: sizePx * 0.4 + 'px',
        backgroundColor: user ? backgroundColor : undefined,
        color: textColor,
      }"
    >
      {{ initials }}
    </span>
    <span v-if="showName && displayName" class="user-initials-name">{{ displayName }}</span>
  </div>
</template>

<style scoped>
.user-initials-avatar-wrapper {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 0;
}

.user-initials-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  font-weight: 600;
  letter-spacing: -0.02em;
  flex-shrink: 0;
  line-height: 1;
  box-sizing: border-box;
}

.avatar-placeholder {
  background-color: var(--n-color-embedded);
  color: var(--n-text-color-3);
  font-weight: 500;
}

.user-initials-name {
  font-size: inherit;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 160px;
}
</style>
